import React, { useMemo } from 'react';
import {
  gql,
  NetworkStatus,
  useLazyQuery,
  usePaginationHelper,
  useQuery,
  useNavigate,
} from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Colors, Spacing } from 'works/legacy/lib/style-helper';
import {
  buildOptionsFromData,
  FilteredTable,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import { PaginatedStaffingRequestsList } from '@applicant-flow/graphql/queries';
import { StaffingRequestType } from '@applicant-flow/types';
import { StateMenu } from 'works/components/state-menu';
import {
  DateRangeFilter,
  DateWithoutTimeZone,
  EmptyContent,
  ErrorHandler,
  NumberRangeFilter,
  Text,
  TextFilterSearch,
  VisualAvailability,
} from '@common/components';
import { StaffingRequestsApprovalsPendingList } from './staffing-requests-approvals-pending-list';
import { OrganizationUnitsClinicalUnits } from 'works/graphql/organization-units-clinical-units';
import { TextSearchTableFilter } from 'works/components/filtered-table/text-search-table-filter';
import { RolesQuery } from '@clinician-profile/graphql';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { RenderShiftType } from '@applicant-flow/components';

type OrganizationUnitType = {
  id: string;
  name: string;
  costCenter: string;
  clinicalUnit: {
    id: string;
    displayName: string;
  };
};

const ORG_BUSINESS_QUERY = gql`
  query StaffingRequestOrganizationBusinesses {
    organizationBusinesses {
      id
      name
      organization {
        id
        name
      }
    }
  }
`;

function formatShift({
  staffingRequestType,
  shiftsPerWeek,
  hoursPerShift,
}: {
  staffingRequestType: string;
  shiftsPerWeek: number;
  hoursPerShift: number;
}) {
  if (staffingRequestType === 'on_demand') {
    return 'Per Diem';
  }

  return `${shiftsPerWeek} x ${hoursPerShift}`;
}

export function StaffingRequestList({
  onDemandEnabled,
}: {
  onDemandEnabled: boolean;
}) {
  const navigate = useNavigate();
  const staffingRequestTypes = onDemandEnabled
    ? ['long_term', 'on_demand']
    : ['long_term'];

  const {
    filters,
    hasAnyFilter,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'StaffingRequestTable',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'REQUEST_CLIENT_STATUS',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      hiringLeader: 'HIRING_LEADER',
      externalId: 'EXTERNAL_ID',
      code: 'ID',
      type: 'TYPE',
      rate: 'RATE',
      role: 'ROLE',
      shiftType: 'SHIFT_TYPE',
      shift: 'SHIFT',
      startDate: 'START',
      state: 'REQUEST_CLIENT_STATUS',
      length: 'LENGTH',
      accepted: 'ACCEPTED',
      inReview: 'IN_REVIEW',
      submissions: 'SUBMISSIONS',
      open: 'OPEN',
      offer: 'OFFER',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
    },
  });

  const [
    fetchOrganizationUnits,
    { error: organizationUnitError, data: organizationUnitData },
  ] = useLazyQuery(OrganizationUnitsClinicalUnits, {
    fetchPolicy: 'cache-first',
  });

  const [fetchRoles, { error: rolesError, data: rolesData }] =
    useLazyQuery(RolesQuery);

  const [
    fetchOrganizationBusiness,
    { error: orgBusinesserror, data: orgBusinessData },
  ] = useLazyQuery(ORG_BUSINESS_QUERY);

  const currentMembership = useCurrentMembership();

  const { loading, error, data, networkStatus } = useQuery(
    PaginatedStaffingRequestsList,
    {
      variables: { ...paginationVariables, staffingRequestTypes },
      notifyOnNetworkStatusChange: true,
      onCompleted() {
        fetchRoles();
        fetchOrganizationUnits();
        fetchOrganizationBusiness();
      },
    }
  );

  const filtersDataReady =
    !!orgBusinessData &&
    !!organizationUnitData &&
    !!currentMembership &&
    !!rolesData;

  const canShowApplicationsSubmitted =
    !!currentMembership.canShowApplicationsSubmitted;

  const columns = useMemo(
    () =>
      [
        {
          title: 'Job ID',
          dataIndex: 'code',
          key: 'code',
          filtered: filters.code || false,
          filteredValue: filters.code || null,
          filterDropdown: TextSearchTableFilter,
          sorter: true,
        },
        {
          title: 'Facility',
          dataIndex: 'organizationBusiness',
          key: 'organizationBusiness',
          filtered: filters.organizationBusiness || false,
          filteredValue: filters.organizationBusiness || null,
          filters: buildOptionsFromData(
            orgBusinessData?.organizationBusinesses,
            (organizationBusiness: { id: string; name: string }) => ({
              value: organizationBusiness.id,
              text: organizationBusiness.name,
            })
          ),
          filterDropdown: MultiSelectTableFilter,
          render: function RenderFacilityCityState(
            _: unknown,
            record: StaffingRequestType
          ) {
            const organizationBusiness =
              record.organizationUnit.department.organizationBusiness;
            return (
              <>
                {organizationBusiness.name}
                <br />
                <span className="secondary-paragraph">
                  {organizationBusiness.city}, {organizationBusiness.stateCode}
                </span>
              </>
            );
          },
          sorter: true,
        },
        {
          title: 'Unit',
          dataIndex: 'organizationUnit',
          key: 'clinicalUnit',
          filtered: filters.clinicalUnit || false,
          filteredValue: filters.clinicalUnit || null,
          filters: buildOptionsFromData(
            organizationUnitData?.organizationUnits,
            (organizationUnit: OrganizationUnitType) => ({
              value: organizationUnit?.clinicalUnit.id,
              text: `${organizationUnit.name} - ${organizationUnit?.costCenter}`,
            }),
            (organizationUnit: OrganizationUnitType) => {
              return organizationUnit?.clinicalUnit.id;
            }
          ),
          filterDropdown: MultiSelectTableFilter,
          render: (organizationUnit: OrganizationUnitType) => (
            <>
              <Text>{organizationUnit?.name}</Text>
              <br />
              <Text color="midnightLighter">
                {organizationUnit?.costCenter}
              </Text>
            </>
          ),
        },
        {
          title: 'Manager',
          key: 'hiringLeader',
          dataIndex: 'hiringLeader',
          sorter: true,
          filtered: filters.hiringLeader || false,
          filteredValue: filters.hiringLeader || null,
          className: 'sentry-mask',
          filterDropdown: TextSearchTableFilter,
          render: (hiringLeader: { name: string }) => <>{hiringLeader.name}</>,
        },
        {
          title: 'External ID',
          dataIndex: 'externalId',
          key: 'externalId',
          width: '105px',
          sorter: true,
          filtered: filters.externalId || false,
          filteredValue: filters.externalId || null,
          filterDropdown: ({
            setSelectedKeys,
            confirm,
          }: FilterDropdownProps) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <TextFilterSearch
                onSearch={(value) => {
                  value ? setSelectedKeys([value]) : setSelectedKeys([]);
                  confirm();
                }}
              />
            </section>
          ),
          render: (externalId: string) => <>{externalId}</>,
        },
        {
          title: 'Role',
          dataIndex: 'role',
          key: 'role',
          sorter: true,
          filtered: filters.role || false,
          filteredValue: filters.role || null,
          filters: buildOptionsFromData(
            rolesData?.roles || [],
            (role: { id: string; abbreviation: string }) => ({
              text: role.abbreviation,
              value: role.id,
            })
          ),
          filterDropdown: MultiSelectTableFilter,
          render: (role: { abbreviation: string }) => <>{role.abbreviation}</>,
        },
        {
          title: 'Shift Type',
          dataIndex: 'shiftType',
          key: 'shiftType',
          width: '100px',
          sorter: true,
          filtered: filters.shiftType || false,
          filteredValue: filters.shiftType || null,
          filterDropdown: MultiSelectTableFilter,
          filters: [
            {
              text: <RenderShiftType shiftType="day" />,
              value: 'day',
            },
            {
              text: <RenderShiftType shiftType="evening" />,
              value: 'evening',
            },
            {
              text: <RenderShiftType shiftType="night" />,
              value: 'night',
            },
            {
              text: <RenderShiftType shiftType="variable" />,
              value: 'variable',
            },
          ],
          render: (shiftType: 'day' | 'evening' | 'night' | 'variable') => (
            <RenderShiftType shiftType={shiftType} />
          ),
        },
        {
          title: 'Shift',
          dataIndex: 'shift',
          key: 'shift',
          sorter: true,
          width: '60px',
          render: (_value: unknown, record: StaffingRequestType) => (
            <>{formatShift(record)}</>
          ),
        },
        {
          title: 'Start',
          dataIndex: 'startDate',
          key: 'startDate',
          sorter: true,
          filterFormat: 'dateRange',
          filtered: filters.startDate || false,
          filteredValue: filters.startDate || null,
          filterDropdown: ({
            setSelectedKeys,
            confirm,
          }: FilterDropdownProps) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <DateRangeFilter
                autoFocus
                onSearch={(value) => {
                  setSelectedKeys((value || []) as string[]);
                  confirm();
                }}
              />
            </section>
          ),
          render: function renderStartDate(startDate: string) {
            return <DateWithoutTimeZone date={startDate} />;
          },
        },
        {
          title: 'Length',
          dataIndex: 'length',
          key: 'length',
          sorter: true,
          filterFormat: 'weeksRange',
          filtered: filters.length || false,
          filteredValue: filters.length || null,
          filterDropdown: ({
            setSelectedKeys,
            confirm,
          }: FilterDropdownProps) => (
            <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
              <NumberRangeFilter
                onSearch={(value) => {
                  value ? setSelectedKeys(value) : setSelectedKeys([]);
                  confirm();
                }}
              />
            </section>
          ),
          render: (length: string) => <>{length} weeks</>,
        },
        {
          title: 'Open',
          dataIndex: 'open',
          key: 'open',
          width: '100px',
          filtered: filters.staffingRequestPositionsCount || false,
          filteredValue: filters.staffingRequestPositionsCount || null,
          sorter: true,
          render: (id: string, record: StaffingRequestType) => {
            const available = record.openStaffingRequestPositionsCount;
            const total = record.staffingRequestPositionsCount;

            return <VisualAvailability available={available} total={total} />;
          },
        },
        {
          title: 'Submitted',
          dataIndex: 'submittedStaffingRequestApplicationsCount',
          key: 'submissions',
          sorter: true,
          hidden: !canShowApplicationsSubmitted,
          render: function displayColoredText(count: number) {
            return <Text color="skyRegular"> {count.toString()}</Text>;
          },
        },
        {
          title: 'Review',
          dataIndex: 'inReviewStaffingRequestApplicationsCount',
          key: 'inReview',
          sorter: true,
          render: function displayColoredText(count: number) {
            return <Text color="harvestGold"> {count.toString()}</Text>;
          },
        },
        {
          title: 'Offer',
          dataIndex: 'offeredOffersStaffingRequestApplicationsCount',
          key: 'offer',
          sorter: true,

          render: function displayColoredText(count: number) {
            return <Text color="grassMid"> {count.toString()}</Text>;
          },
        },
        {
          title: 'Status',
          dataIndex: 'state',
          key: 'state',
          sorter: true,
          width: '140px',
          defaultSortOrder: 'ascend',
          filtered: filters.state || false,
          filteredValue: filters.state || null,
          filters: [
            {
              text: 'Open',
              value: 'OPEN',
            },
            {
              text: 'Filled',
              value: 'FILLED',
            },
            {
              text: 'On Hold',
              value: 'ON_HOLD',
            },
            {
              text: 'Cancelled',
              value: 'CANCELLED',
            },
            {
              text: 'Pending',
              value: 'PENDING',
            },
            {
              text: 'Not Approved',
              value: 'REJECTED_BY_APPROVER',
            },
          ],
          render: function StateWithDate(
            _value: unknown,
            staffingRequest: StaffingRequestType
          ) {
            return (
              <StateMenu staffingRequest={staffingRequest} viewMode="list" />
            );
          },
        },
      ].filter((item) => !item.hidden),
    [
      filters.code,
      filters.clinicalUnit,
      filters.externalId,
      filters.hiringLeader,
      filters.length,
      filters.organizationBusiness,
      filters.role,
      filters.shiftType,
      filters.staffingRequestPositionsCount,
      filters.startDate,
      filters.state,
      orgBusinessData?.organizationBusinesses,
      organizationUnitData?.organizationUnits,
      rolesData?.roles,
      canShowApplicationsSubmitted,
    ]
  );

  const anyError =
    error || organizationUnitError || orgBusinesserror || rolesError;
  if (anyError) {
    return <ErrorHandler error={anyError} log="StaffingRequestList" />;
  }

  const isLoaded = Boolean(networkStatus === NetworkStatus.ready && data);
  const paginationType = 'paginatedStaffingRequests';
  const recordType = 'staffingRequests';

  const records = isLoaded && data ? data[paginationType][recordType] : [];
  const total =
    data && data[paginationType] ? data[paginationType].totalCount : null;

  if (isLoaded && !hasAnyFilter && records && records.length === 0) {
    return (
      <>
        <EmptyContent
          imagePath="./staffing/empty-requests.png"
          header="Hello! You might be new around here."
          text="Add a Staffing Request to begin your hiring journey with Works, let's go!"
        />
      </>
    );
  }

  const stateClassMap = {
    OPEN: '',
    PENDING: '',
    CANCELLED: '',
    FILLED: 'disabled-row',
    ON_HOLD: 'on-hold-row',
    REJECTED_BY_APPROVER: '',
  };

  const staffingRequests = records.map((request) => ({
    ...request,
    rowClassName: stateClassMap[request.state],
  }));

  return (
    <section className="staffingRequestList">
      <StaffingRequestsApprovalsPendingList columns={columns} />
      <FilteredTable
        filters={undefined}
        rowKey="id"
        columns={columns}
        loading={loading}
        dataSource={staffingRequests}
        onChange={tableChangeHandler}
        onRow={(record: StaffingRequestType) => {
          return {
            onClick: () => {
              navigate(`/works/staffing-requests/${record.id}`);
            },
          };
        }}
        filtersDataReady={filtersDataReady}
        onFilterChange={filterChangeHandler}
        localStorageFilterKey={localStorageFilterKey}
        pagination={{
          ...paginationVariables,
          total,
        }}
      />
      <style jsx>{`
        :global(.staffingRequestList .dot) {
          min-width: 12px;
          min-height: 12px;
          background-color: ${Colors.coral};
          border-radius: 100%;
          margin-right: 8px;
        }
      `}</style>
    </section>
  );
}
